import { Component, createMemo, createResource, createSignal, For, Show } from "solid-js";
import {
  Match,
  TournamentCivStats,
  TournamentData,
  TournamentMapStats,
  TournamentPlayerStats,
  TOURNAMENT_CIV_ABBRS,
} from "../types/tournaments";
import { civs as civMap } from "../utils/civs";
import { mapMap } from "../utils/maps";
import { fetchResults, parseMatchesFromWikiSyntax } from "../utils/parse-lqp-games";
import { getTournamentStats } from "../utils/stats";
import { CivIcon } from "./common";

const CivStats: Component<{ civs: TournamentCivStats[] }> = (props) => {
  return (
    <div class="bg-gray-700 sm:rounded-2xl pt-4 basis-1/2 ">
      <h2 class="text-white text-2xl mb-4 p-6 font-bold">Civilizations</h2>
      <div class="overflow-x-auto max-w-[100vw]">
        <table class="w-full">
          <thead>
            <tr class="text-gray-400 text-left text-xs uppercase tracking-wider">
              <th class="text-left pl-6 mr-4" py-1>
                civilization
              </th>
              <th class="text-right pr-2 mr-4">Winrate</th>
              <th class="text-right pr-2 mr-4">Wins</th>
              <th class="text-right pr-2 mr-4">Losses</th>
              <th class="text-right pr-2 mr-4">Mirrors</th>
              <th class="text-right pr-6">Played</th>
              <th class="text-right pr-6">Bans</th>
            </tr>
          </thead>
          <For each={props.civs}>
            {(s) => (
              <tr class="odd:bg-gray-800/50">
                <td class="px-6 py-4 mr-4 flex font-bold whitespace-nowrap">
                  <CivIcon class="mr-4" civ={s.civ} />
                  {civMap[s.civ].label}
                </td>

                <td class="mr-4 py-2 text-right ">
                  {s.played == 0 ? '--' : s.winrate.toLocaleString("en-US", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                    style: "percent",
                  })}
                </td>

                <td class="mr-4 py-2 px-2  text-right text-green-500">{s.wins}W</td>
                <td class="mr-4 py-2 px-2  text-right text-red-500">{s.losses}L</td>
                <td class="mr-4 py-2 px-2 text-right text-gray-400">{s.mirrors}M</td>
                <td class="mr-4 py-2 pl-2 pr-6 text-right text-gray-300">{s.played}</td>
                <td class="mr-4 py-2 pl-2 pr-6 text-right text-gray-300">{s.bans}</td>
                <td class="pr-6 w-auto"></td>
              </tr>
            )}
          </For>
        </table>
      </div>
    </div>
  );
};
const civs = TOURNAMENT_CIV_ABBRS;

const CivHeaderCells = () => {
  return (
    <For each={civs}>
      {(civ) => (
        <th class="p-2">
          <CivIcon civ={civ} />
        </th>
      )}
    </For>
  );
};
const CivCells: Component<{ civs: TournamentMapStats["civs"] }> = (props) => {
  return (
    <For each={civs}>
      {(civ) => (
        <td class="pr-4">
          <div class="flex gap-2">
            <span class="text-green-500" classList={{ "opacity-25": props.civs[civ].wins == 0 }}>
              {props.civs[civ].wins}W
            </span>
            <span class="text-red-500" classList={{ "opacity-25": props.civs[civ].losses == 0 }}>
              {props.civs[civ].losses}L
            </span>
          </div>
        </td>
      )}
    </For>
  );
};

const Maps: Component<{ maps: TournamentMapStats[] }> = (props) => {
  return (
    <div class="bg-gray-700 sm:rounded-2xl pt-4 flex flex-col basis-3/5 relative">
      <h2 class="text-white text-2xl mb-4 p-6 font-bold">Maps</h2>
      <div class="relative flex-auto overflow-auto w-full">
        <table class="w-full">
          <thead>
            <tr class="text-gray-400 text-left text-xs uppercase tracking-wider">
              <th class="text-left pl-6">Player</th>
              <th class="text-left">Played</th>
              <th class="text-left">Bans</th>
              <CivHeaderCells />
            </tr>
          </thead>
          <For each={props.maps}>
            {(map) => (
              <tr class="odd:bg-gray-800/50">
                <td class="px-6 py-4 flex gap-4 font-bold">
                  {mapMap[map.map] ? (
                    <img
                      src={mapMap[map.map] ?? null}
                      class="w-6 h-6 inline-block outline-item-map outline outline-1 outline-offset-1 rounded mr-2 "
                    />
                  ) : (
                    <div class="w-6 h-6 inline-block outline-item-map bg-item-map/30 outline outline-1 outline-offset-1 rounded mr-2 "></div>
                  )}
                  <span class="truncate">{map.map}</span>
                </td>
                <td class="pr-4">
                  <div class="flex gap-2">
                    <span class="text-gray-200">{map.played}</span>
                  </div>
                </td>
                <td class="pr-4">
                  <div class="flex gap-2">
                    <span class="text-gray-200">{map.bans}</span>
                  </div>
                </td>
                <CivCells civs={map.civs} />
              </tr>
            )}
          </For>
        </table>
      </div>
    </div>
  );
};

const Teams: Component<{ teams: TournamentPlayerStats[] }> = (props) => {
  return (
    <div class="bg-gray-700 sm:rounded-2xl pt-4 flex flex-col basis-3/5 relative">
      <h2 class="text-white text-2xl mb-4 p-6 font-bold">Teams</h2>
      <div class="relative flex-auto overflow-auto w-full">
        <table class="w-full">
          <thead>
            <tr class="text-gray-400 text-left text-xs uppercase tracking-wider">
              <th class="text-left pl-6">Team</th>
              <th class="text-left">Total</th>
              <CivHeaderCells />
            </tr>
          </thead>
          <For each={props.teams}>
            {(player) => (
              <tr class="odd:bg-gray-800/50">
                <td class="px-6 py-4 flex gap-4 font-bold">{player.name}</td>
                <td class="pr-4">
                  <div class="flex gap-2">
                    <span class="text-green-500" classList={{ "opacity-25": player.wins == 0 }}>
                      {player.wins}W
                    </span>
                    <span class="text-red-500" classList={{ "opacity-25": player.losses == 0 }}>
                      {player.losses}L
                    </span>
                  </div>
                </td>
                <CivCells civs={player.civs} />
              </tr>
            )}
          </For>
        </table>
      </div>
    </div>
  );
};

const Players: Component<{ players: TournamentPlayerStats[] }> = (props) => {
  return (
    <div class="bg-gray-700 sm:rounded-2xl pt-4 flex flex-col basis-3/5 relative">
      <h2 class="text-white text-2xl mb-4 p-6 font-bold">Players</h2>
      <div class="relative flex-auto overflow-auto w-full">
        <table class="w-full">
          <thead>
            <tr class="text-gray-400 text-left text-xs uppercase tracking-wider">
              <th class="text-left pl-6">Player</th>
              <th class="text-left">Total</th>
              <CivHeaderCells />
            </tr>
          </thead>
          <For each={props.players}>
            {(player) => (
              <tr class="odd:bg-gray-800/50">
                <td class="px-6 py-4 flex gap-4 font-bold">{player.name}</td>
                <td class="pr-4">
                  <div class="flex gap-2">
                    <span class="text-green-500" classList={{ "opacity-25": player.wins == 0 }}>
                      {player.wins}W
                    </span>
                    <span class="text-red-500" classList={{ "opacity-25": player.losses == 0 }}>
                      {player.losses}L
                    </span>
                  </div>
                </td>
                <CivCells civs={player.civs} />
              </tr>
            )}
          </For>
        </table>
      </div>
    </div>
  );
};

const filterCssClass = (active: boolean) =>
  `rounded-full border border-gray-400 text-xs uppercase px-4 py-1 font-bold hover:text-white transition ${
    active ? "text-gray-50 bg-gray-400" : "text-gray-200"
  }`;
export const TournamentStats: Component<{ url?: string; tournamentId?: number | string; }> = (
  props
) => {
  const [filters, setFilters] = createSignal<string[]>([]);
  
  const [data] = createResource(async () => {
    return await fetchResults(props.url ?? `/api/v0/tournaments/${props.tournamentId}/results`);
  });

  const groups = () => {
    const matches = data() && Object.entries(data()?.results || {}).reduce((g, [label, parts]) => {
      const matches = Object.values(parts).flatMap(parseMatchesFromWikiSyntax);
      if (matches.length > 0) g.push({ label, matches });
      return g;
    }, [] as { label: string; matches: Match[] }[]);
    return matches;
  };

  const stats = createMemo(() => {
    const filtered = filters().length ? groups()?.filter(({ label }) => filters().includes(label)) : groups();
    return filtered?.length ? getTournamentStats(filtered.flatMap((m) => m.matches)) : null;
  });

  const toggleGroup = (group: string) => {
    setFilters(filters().includes(group) ? filters().filter((f) => f !== group) : [...filters(), group]);
  };
  return (
    <div>
      {data()?.liquipedia_updated_at && <div class="relative"><div class="absolute right-2 bottom-0 m-2 text-gray-400">Last updated: {new Date(data().liquipedia_updated_at).toLocaleString()}</div></div>}
      <Show when={data.loading}>
        <div class="my-12 mb-12 bg-gray-700 sm:rounded-2xl px-4 py-12 text-center">
          <h2 class="font-bold text-lg text-gray-300">Loading results</h2>
          <small class="text-md text-gray-300">
            Currently crunching the numbers...
          </small>
        </div>
      </Show>
      {groups()?.length > 1 && (
        <div class="bg-gray-700 sm:rounded p-4 flex flex-wrap gap-2 items-center my-4">
          <button onClick={() => setFilters([])} class={filterCssClass(!filters()?.length)}>
            All
          </button>
          <For each={groups()}>
            {(group) => (
              <button onClick={() => toggleGroup(group.label)} class={filterCssClass(filters().includes(group.label))}>
                {group.label}
              </button>
            )}
          </For>
        </div>
      )}
      <Show when={stats()}>
        {(stat) => (
          <div class="space-y-5 my-5">
            <CivStats civs={stat().civs} />
            <Maps maps={stat().maps} />
            <Show when={stats().hasTeams}>
              <Teams teams={stat().teams} />
            </Show>
            <Players players={stat().players} />
          </div>
        )}
      </Show>
      <Show when={groups() && !stats()?.players.length}>
        <div class="my-12 mb-12 bg-gray-700 sm:rounded-2xl px-4 py-12 text-center">
          <i class="fas fa-circle-exclamation text-2xl text-gray-400 mb-4"></i>
          <h2 class="font-bold text-lg text-gray-300">Could not get any statistics from Liquipedia</h2>
          <small class="text-md text-gray-300">
            Possibly results are not synced yet or the (game) format is not understood.
          </small>
        </div>
      </Show>
    </div>
  );
};
